import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Children } from 'react';

const REQUIRED_CHILD_COUNT = 2;

function normalizeChildren(children) {
  const items = Children.toArray(children);

  if (items.length !== REQUIRED_CHILD_COUNT) {
    if (import.meta.env.MODE !== 'production') {
      console.warn(
        `InlineRangeGroup expects exactly ${REQUIRED_CHILD_COUNT} children, received ${items.length}.`,
      );
    }
  }

  return items;
}

/**
 * InlineRangeGroup
 *
 * Layout wrapper for paired inputs rendered inline.
 *
 * @param {Object} props
 * @param {string} [props.label]
 *   Group label rendered above inputs.
 * @param {React.ReactNode} props.children
 *   Exactly two elements rendered side by side.
 * @param {React.ReactNode|string|null} [props.separator]
 *   Optional content rendered between the two inputs.
 * @param {boolean} [props.error=false]
 *   Whether to display a group-level error.
 * @param {string} [props.errorMessage]
 *   Error message shown below the inputs.
 * @param {Object} [props.sx]
 *   Styles applied to the root container.
 *
 * @returns {React.ReactElement}
 */
export default function InlineRangeGroup(props: any) {
  const { label, children, separator, error = false, errorMessage, sx = {} } = props;

  const items = normalizeChildren(children);
  const showSeparator = Boolean(separator);

  return (
    <Stack gap={0.5} sx={sx}>
      {label && (
        <Typography variant='body1' fontWeight={500}>
          {label}
        </Typography>
      )}

      <Stack direction='row' gap={2} alignItems='flex-start'>
        {items[0]}

        {showSeparator && items.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 40,
              color: 'text.secondary',
              userSelect: 'none',
            }}
          >
            {separator}
          </Box>
        )}

        {items[1]}
      </Stack>

      {error && errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
    </Stack>
  );
}
